import './style.scss';

import { Divider, Modal } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { PaymentFooter } from '../../../assets/images';
import { TRACKING_CONSTANTS } from '../../../model/constants';
import { cancelSpecial } from '../../../recoil/atoms/cancelSpecial';
import { userProfile } from '../../../recoil/selectors/userProfile';
import { getToken } from '../../../services/token-service/token.service';
import { trackPaymentActivity } from '../../../services/tracking/tracking.service';
import { useRouteName } from '../../../util/tracking';
import { useGetAllPackages } from '../../services/packages/useGetAllPackages';
import { CreditsCard } from '../shared/CreditsCard/CreditsCard';

export const SpecialOfferModal = () => {
  // this is still needed due to strange logic in the Cards component...
  // however, it looks like it is a potential cause for not showing the modal when it should...
  const [shallCancelSpecial, setShallCancelSpecial] = useRecoilState(cancelSpecial);
  const { data } = useGetAllPackages();
  const currentPage = useRouteName();
  const profile = useRecoilValue(userProfile);
  const [show, setShow] = useState<boolean>(false);
  const specialOffer = JSON.parse(JSON.parse(JSON.stringify(localStorage.getItem('special'))));
  // to avoid stacking multiple timeouts, we use a ref to store the timeout id
  const displayTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // if currently there is no pending special offer render
    if (!displayTimeoutRef.current) {
      // if there is a pending special offer and the user is logged in
      if (profile?.id && specialOffer) {
        displayTimeoutRef.current = setTimeout(() => {
          setShow(specialOffer.special);
        }, 30000);
      }
    } else {
      // if the special offer display is canceled, or the user is not logged in anymore
      if (shallCancelSpecial || !profile?.id) {
        setShow(false);
        clearTimeout(displayTimeoutRef.current);
      }
    }
  }, [shallCancelSpecial, specialOffer, profile?.id]);

  const trackingData = useMemo(
    () => ({
      place: currentPage,
      via: TRACKING_CONSTANTS.VIA.SPECIAL_OFFER_MODAL
    }),
    [currentPage]
  );

  useEffect(() => {
    if (show && data) {
      const token = getToken();

      trackPaymentActivity(
        {
          package_type: TRACKING_CONSTANTS.PACKAGE_TYPES.SPECIAL_OFFER,
          ...trackingData
        },
        token
      );
    }
  }, [show, data, trackingData]);

  const renderedPackages = useMemo(() => {
    return (data?.filter((item) => item.is_special_offer === 1) ?? []).map((pkg) => (
      <CreditsCard
        redirectUrl={location.href}
        packageDef={pkg}
        key={pkg.id}
        version={1}
        trackingData={trackingData}
      />
    ));
  }, [data, trackingData]);

  const closeModal = () => {
    localStorage.setItem('special', JSON.stringify({ ...specialOffer, special: false }));
    setShow(false);
    // possibly this is not needed anymore due to not having stacked modal show timers;
    // however, I do not want to risk breaking something (^_^)
    setShallCancelSpecial(true);
  };

  const shouldShow = useMemo(() => {
    if (shallCancelSpecial) return false;
    if (!profile?.id) return false; // no user data means the user is not logged in
    return show && renderedPackages.length > 0;
  }, [profile?.id, renderedPackages.length, shallCancelSpecial, show]);

  return (
    <Modal
      footer={
        <div className={'modal-footer'}>
          <img src={PaymentFooter} alt={'footer'} />
        </div>
      }
      open={shouldShow}
      onCancel={closeModal}
      maskClosable={false}
      bodyStyle={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      className="special-offer"
    >
      <div className={'header'}>
        <span>{profile?.username}</span>, this special offer
        <br /> is just for you!
      </div>
      <div className={'hurry'}>Hurry up, this is a one time offer</div>
      {renderedPackages}
      <Divider dashed />
    </Modal>
  );
};
